import { Component } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { actions, Form } from 'forms/rrf';
import { Col, Container, Row } from 'react-bootstrap';
import ImageUpload from '../common/form/elements/image-upload';
import PageTitle from '../common/page-title';
import ConfirmPopup from '../common/confirm-popup';
import ArrowButton from '../common/button-arrow';
import InlineCKEditor from '../common/editor/inline-editor';
import { closeAccount, getMyProfile, saveMyProfile, updateMyProfileEmail } from '../../actions/user';
import { loadPageContent } from '../../actions/content';
import MarketingPreferences from '../about-you/marketing-preferences';
import PersonalDetails from './personal-details';
import CommunicationPreferences from '../common/form/user/communication-preferences';
import CommunicationDetails from '../common/form/user/communication-details';
import UpdateSecurity from './update-security';
import { USER_TYPE_REP, USER_TYPE_RETAILER } from '../../data/userTypes';
import ChangeEmailDialog from '../admin/my-profile/change-email-dialog';
import { currentUserGroup, isPseudoLoggedIn } from '../../utils/tokenUtils';

import './style.css';

const logoutUrls = {
  [USER_TYPE_RETAILER]: '/logout',
  [USER_TYPE_REP]: '/team/logout',
};

class MyProfile extends Component {
  constructor(props) {
    super(props);

    this.pageSlug = 'my-profile';
    this.state = {
      showDelete: false,
      image: '',
      isDialogOpen: false,
      originalEmail: '',
      payload: null
    };
  }

  componentDidMount() {
    const { dispatch, location: { search } } = this.props;
    const token = new URLSearchParams(search).get('email_change_token');

    this.resetChangeEmailDialogFields();

    if (token) {
      dispatch(updateMyProfileEmail(token, false));
    } else {
      dispatch(getMyProfile());
    }

    dispatch(loadPageContent(`api/pages/${this.pageSlug}`));
  }

  componentDidUpdate(prevProps) {
    const { isLoading } = this.props;

    if (prevProps.isLoading !== isLoading && !isLoading) {
      const { profile: { attributes: { email } } } = this.props;

      this.loadFormData();
      this.setState({ originalEmail: email });
    }
  }

  loadFormData = () => {
    const { dispatch, profile: { attributes } } = this.props;
    const marketingCategories = {};
    Object.values(attributes['marketing-categories']).forEach((value) => {
      marketingCategories[value.id] = { value: value.accepted };
    });
    const data = {
      personal_address_1: attributes['personal-address-1'] || undefined,
      personal_address_2: attributes['personal-address-2'] || undefined,
      personal_address_3: attributes['personal-address-3'] || undefined,
      personal_address_4: attributes['personal-address-4'] || undefined,
      personal_eircode: attributes['personal-eircode'] || undefined,
      comms_email: attributes['comms-email'] || false,
      comms_mobile: attributes['comms-mobile'] || false,
      email: attributes.email || undefined,
      mobile: attributes.mobile || undefined,
      should_opt_in: attributes['should-opt-in'] || false,
      marketing_categories: marketingCategories || [],
      has_ignite_gold_outlet: attributes['has-ignite-gold-outlet'],
      has_mini_chain_outlet: attributes['has-mini-chain-outlet']
    };

    dispatch(actions.load('deep.my_profile', data));
  }

  onSubmit = (payload) => {
    const { dispatch } = this.props;
    const { originalEmail } = this.state;
    const payLoad = { ...payload };

    if (originalEmail !== payLoad.email) {
      this.handleOpenDialog(payLoad);
    } else {
      dispatch(actions.submitFields('deep.my_profile', saveMyProfile(payLoad)));
    }
  };

  handleOpenDialog = (payload) => {
    this.setState({ isDialogOpen: true, payload });
  }

  handleCloseDialog = () => {
    this.setState({ isDialogOpen: false, payload: null }, () => {
      this.resetChangeEmailDialogFields();
    });
  }

  resetChangeEmailDialogFields = () => {
    const { dispatch } = this.props;

    dispatch(actions.change('deep.my_profile_change_email_dialog.password', ''));
    dispatch(actions.reset('deep.my_profile_change_email_dialog'));
  }

  onDelete = (result) => {
    const { dispatch } = this.props;

    this.setState({ showDelete: false });

    if (result) {
      dispatch(closeAccount);
    }
  };

  onImageLoaded = (data) => {
    this.setState({ image: data });
  };

  handleContentSaved = () => {
    const { dispatch } = this.props;

    dispatch(loadPageContent(`api/pages/${this.pageSlug}`));
  };

  onCloseAccount = () => {
    this.setState({
      showDelete: !this.state.showDelete
    });
  };

  handleSubmitDialog = (result) => {
    if (result) {
      const { changeEmailDialogForm, dispatch } = this.props;
      const { payload } = this.state;
      const password = changeEmailDialogForm.password.value;

      dispatch(actions.submitFields('deep.my_profile', saveMyProfile(payload, password)));
    }

    this.handleCloseDialog();
  }

  pageTitleComponent = () => (
    <Row>
      <Col className="my-profile-title" lg={12}>
        <PageTitle>
          My profile
        </PageTitle>
      </Col>
    </Row>
  );

  render() {
    const {
      contents, formValid, redirectAfterDelete, area,
      commsMobile, commsEmail, anyMarketingAccepted, isLoading
    } = this.props;
    const { showDelete, isDialogOpen } = this.state;
    const isUserNormalLoggedIn = !isPseudoLoggedIn();
    const isSaveDisabled = !formValid || !isUserNormalLoggedIn;
    const logoutUrl = area ? logoutUrls[area] : logoutUrls[USER_TYPE_RETAILER];
    const profile = this.props.profile.attributes;
    const image = profile.links ? profile.links.image : undefined;

    if (isLoading) {
      return (
        <Container fluid className="my-profile user-inner-container">
          {this.pageTitleComponent()}
          <Row>
            <Col lg={12}>
              Loading...
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <div>
        <Container fluid className="my-profile user-inner-container">
          {this.pageTitleComponent()}
          <Row>
            <Col className="my-profile-editable-content" lg={12}>
              <InlineCKEditor
                identifier={`${this.pageSlug}_content_text`}
                onContentSaved={this.handleContentSaved}
                contents={contents[`${this.pageSlug}_content_text`]}
              />
            </Col>
          </Row>
          <Form model="deep.my_profile" onSubmit={payload => this.onSubmit(payload)}>
            <Row className="show-grid my-profile-details">
              <Col className="my-profile-details-image" md={4}>
                <ImageUpload
                  onImageLoaded={this.onImageLoaded}
                  source={image}
                  sizeLimit={1024 * 1024}
                  type={['image/png', 'image/jpg', 'image/jpeg']}
                  upload={area === USER_TYPE_REP}
                />
              </Col>
              <Col className="my-profile-details-personal" md={5}>
                <PersonalDetails profile={profile} type={area}/>
                {area !== USER_TYPE_REP && (
                <div className="my-profile-details-personal-communication">
                  <CommunicationDetails />
                </div>
                )}
              </Col>
              {isUserNormalLoggedIn && (
                <Col md={3} className="ta-righ my-profile-details-buttons">
                  <UpdateSecurity type={area}/>
                </Col>
              )}
            </Row>
            {area !== USER_TYPE_REP && (
              <div>
                <hr className="light"/>
                {isUserNormalLoggedIn && (
                  <Row className="my-profile-marketing">
                    <Col lg={12}>
                      <MarketingPreferences
                        title={(
                          <label>MARKETING PREFERENCES</label>
                        )}
                        type="update"
                      />
                    </Col>
                    {anyMarketingAccepted && (
                      <Col className="my-profile-marketing-contacts" lg={12}>
                        <CommunicationPreferences
                          commsEmail={commsEmail}
                          commsMobile={commsMobile}
                          model={'deep.my_profile'}
                          marketing
                        />
                      </Col>
                    )}
                  </Row>
                ) }
                <Row className="my-profile-save">
                  <ArrowButton color="grey" className="float-end" disabled={isSaveDisabled}>
                    Save
                  </ArrowButton>
                  {currentUserGroup() === 'staff' && (
                    <ArrowButton
                      color="red"
                      className="float-end close-account-button"
                      arrow={false}
                      submit={false}
                      onClick={this.onCloseAccount}
                    >
                      Close account
                    </ArrowButton>
                  )}
                  {showDelete && (
                    <ConfirmPopup onClose={this.onDelete}>
                      <p>Are you sure you want to close your account?
                        If you close your account you will no longer be able to access <b>ignite</b>
                      </p>
                    </ConfirmPopup>
                  )}
                  {redirectAfterDelete && <Navigate to={logoutUrl} replace/>}
                </Row>
              </div>
            )}
          </Form>
        </Container>
        {isDialogOpen && <ChangeEmailDialog handleCloseDialog={this.handleSubmitDialog} />}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  isLoading: !state.currentUser.fetched && state.currentUser.fetching,
  profile: state.currentUser.profile,
  formValid: state.deep.forms.my_profile.$form.valid,
  redirectAfterDelete: state.currentUser.redirectAfterDelete,
  contents: state.content.list,
  commsEmail: !!state.deep.my_profile.comms_email,
  commsMobile: !!state.deep.my_profile.comms_mobile,
  anyMarketingAccepted: Object.values(state.deep.my_profile.marketing_categories).some(element => element.value),
  changeEmailDialogForm: state.deep.forms.my_profile_change_email_dialog
});

export default connect(mapStateToProps)(MyProfile);
